import { useEffect } from 'react';
import { Command, Search } from 'lucide-react';
import { Button, cn } from '@command-center/ui-system';

export function ClientSidebarCommandButton({
  compact,
  onOpen,
  searchEnabled = true,
}: {
  compact: boolean;
  onOpen: () => void;
  searchEnabled?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      className={cn(
        'h-10 w-full justify-start border-border bg-background px-3 text-muted-foreground shadow-sm hover:text-foreground',
        compact && 'justify-center px-0',
      )}
      onClick={onOpen}
      aria-label="Open command palette"
    >
      <Search className="h-4 w-4 shrink-0" aria-hidden />
      {!compact && (
        <>
          <span className="truncate text-xs">{searchEnabled ? 'Search or run a command' : 'Run a command'}</span>
          <kbd className="ml-auto inline-flex items-center gap-0.5 rounded border bg-muted px-1.5 py-0.5 text-xs">
            <Command className="h-3 w-3" aria-hidden />K
          </kbd>
        </>
      )}
    </Button>
  );
}

export function ClientGlobalShortcuts({ onOpen }: { onOpen(initialQuery?: string): void }) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onOpen('');
        return;
      }
      if (isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === '/') {
        event.preventDefault();
        onOpen('/');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen]);
  return null;
}

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || target instanceof HTMLSelectElement
    || (target instanceof HTMLElement && target.isContentEditable);
}
